/* ============================================================
   Tax & social insurance calculations for Japan
   ============================================================
   References (verify annually):
   - Shakai hoken: kyokai-kenpo.or.jp (rates vary by prefecture)
   - National pension premium: nenkin.go.jp 
   - Income tax brackets: nta.go.jp
   - Dattai ichijikin formula: nenkin.go.jp/international
   
   Rates current as of FY2025-2026.
   ============================================================ */

// ---------- Shakai hoken (employee insurance) ----------
// Employee share. Health is Kyoto reference (10.13% total ÷ 2);
// other prefectures range ~9.5% to ~10.5%.
export const SHAKAI = {
  health: 0.0506,
  pension: 0.0915,
  employment: 0.006,
  care: 0.008, // applied if age >= 40
};

// ---------- Kokumin nenkin ----------
// FY2025 premium — verify each April
export const KOKUMIN_NENKIN_MONTHLY = 17510;

// ---------- Salary income deduction (給与所得控除) ----------
// Annual JPY. Effective FY2020+
export function salaryIncomeDeduction(annualGross) {
  if (annualGross <= 1_625_000) return 550_000;
  if (annualGross <= 1_800_000) return annualGross * 0.4 - 100_000;
  if (annualGross <= 3_600_000) return annualGross * 0.3 + 80_000;
  if (annualGross <= 6_600_000) return annualGross * 0.2 + 440_000;
  if (annualGross <= 8_500_000) return annualGross * 0.1 + 1_100_000;
  return 1_950_000;
}

// ---------- National income tax brackets (FY2024) ----------
const INCOME_TAX_BRACKETS = [
  { max: 1_950_000, rate: 0.05, sub: 0 },
  { max: 3_300_000, rate: 0.10, sub: 97_500 },
  { max: 6_950_000, rate: 0.20, sub: 427_500 },
  { max: 9_000_000, rate: 0.23, sub: 636_000 },
  { max: 18_000_000, rate: 0.33, sub: 1_536_000 },
  { max: 40_000_000, rate: 0.40, sub: 2_796_000 },
  { max: Infinity, rate: 0.45, sub: 4_796_000 },
];

const RECONSTRUCTION_SURTAX = 1.021;

// ---------- Main take-home calculation ----------
export function calculateTakeHome({
  monthlyGross,
  age = 30,
  insuranceType = "shakai", // 'shakai' | 'kokumin'
  dependents = 0,
  isFirstYearResident = false, // resident tax is ¥0 for true first-year residents
}) {
  const annualGross = monthlyGross * 12;

  // Social insurance contributions
  let annualSocialIns;
  if (insuranceType === "shakai") {
    let rate = SHAKAI.health + SHAKAI.pension + SHAKAI.employment;
    if (age >= 40) rate += SHAKAI.care;
    annualSocialIns = annualGross * rate;
  } else {
    // Kokumin: nenkin (flat) + kenko hoken (rough national avg ~10%, capped)
    // True kokumin kenko hoken varies massively by city. This is a rough estimate.
    const nenkin = KOKUMIN_NENKIN_MONTHLY * 12;
    const kenkoHoken = Math.min(annualGross * 0.1, 1_020_000);
    annualSocialIns = nenkin + kenkoHoken;
  }

  // Taxable income for national tax
  const salaryDed = salaryIncomeDeduction(annualGross);
  const basicDed = 480_000;
  const depDed = dependents * 380_000;
  const taxable = Math.max(
    0,
    annualGross - salaryDed - annualSocialIns - basicDed - depDed
  );

  let nationalTax = 0;
  for (const b of INCOME_TAX_BRACKETS) {
    if (taxable <= b.max) {
      nationalTax = Math.max(0, taxable * b.rate - b.sub);
      break;
    }
  }
  nationalTax *= RECONSTRUCTION_SURTAX;

  // Resident tax (住民税) — approximate flat 10% + per-capita
  // Note: first-year residents in Japan pay ¥0 resident tax (based on prev year)
  let residentTax = 0;
  if (!isFirstYearResident) {
    const resTaxable = Math.max(
      0,
      annualGross - salaryDed - annualSocialIns - 430_000 - dependents * 330_000
    );
    residentTax = resTaxable * 0.1 + 5_000;
  }

  const totalDeductions = annualSocialIns + nationalTax + residentTax;
  const annualNet = annualGross - totalDeductions;

  return {
    annualGross,
    annualNet,
    monthlyGross,
    monthlyNet: annualNet / 12,
    breakdown: {
      socialInsurance: annualSocialIns / 12,
      incomeTax: nationalTax / 12,
      residentTax: residentTax / 12,
    },
    annualBreakdown: {
      socialInsurance: annualSocialIns,
      incomeTax: nationalTax,
      residentTax: residentTax,
    },
    effectiveRate: (totalDeductions / annualGross) * 100,
  };
}

// ---------- Dattai ichijikin (lump-sum withdrawal) ----------
// Reference: nenkin.go.jp/international 
// FY2025 rates. Cap is 60 months (5 years) as of 2026.
// Expansion to 96 months proposed but not yet enacted.

// Payment rate (Employees' Pension) by months contributed
// Formula: (premium rate ÷ 2) × calculation factor
// Where calculation factor follows the bracket below
export const KOSEI_NENKIN_FACTORS = [
  { months: 6, factor: 0.5 },
  { months: 12, factor: 1.1 },
  { months: 18, factor: 1.7 },
  { months: 24, factor: 2.2 },
  { months: 30, factor: 2.8 },
  { months: 36, factor: 3.3 },
  { months: 42, factor: 3.9 },
  { months: 48, factor: 4.4 },
  { months: 54, factor: 5.0 },
  { months: 60, factor: 5.5 }, // cap
];

// National Pension lump-sum amounts (FY2025) — fixed by months bracket
// Reference: nenkin.go.jp
export const KOKUMIN_NENKIN_LUMPSUM = [
  { months: 6, amount: 53_775 },
  { months: 12, amount: 107_550 },
  { months: 18, amount: 161_325 },
  { months: 24, amount: 215_100 },
  { months: 30, amount: 268_875 },
  { months: 36, amount: 322_650 },
  { months: 42, amount: 376_425 },
  { months: 48, amount: 430_200 },
  { months: 54, amount: 483_975 },
  { months: 60, amount: 537_750 },
];

export function calculateDattaiKosei({ averageMonthlySalary, monthsContributed }) {
  // Cap at 60 months
  const m = Math.min(monthsContributed, 60);
  
  // Find appropriate factor bracket
  let factor = 0;
  for (const f of KOSEI_NENKIN_FACTORS) {
    if (m >= f.months) factor = f.factor;
  }
  if (m < 6) return { eligible: false, gross: 0, withheld: 0, net: 0, factor: 0 };

  // ASR × payment rate, where payment rate = factor
  const gross = averageMonthlySalary * factor;
  const withheld = gross * 0.2042; // 20.42% non-resident tax
  const net = gross - withheld;

  return {
    eligible: true,
    factor,
    monthsCounted: m,
    monthsLost: Math.max(0, monthsContributed - 60),
    gross,
    withheld,
    net,
    recoverable: withheld, // recoverable via tax representative
  };
}

export function calculateDattaiKokumin({ monthsContributed }) {
  const m = Math.min(monthsContributed, 60);
  if (m < 6) return { eligible: false, amount: 0 };
  
  let amount = 0;
  for (const entry of KOKUMIN_NENKIN_LUMPSUM) {
    if (m >= entry.months) amount = entry.amount;
  }
  return {
    eligible: true,
    amount,
    monthsCounted: m,
    monthsLost: Math.max(0, monthsContributed - 60),
  };
}

// ---------- Future value projection ----------
export function projectFutureValue({
  monthlyContribution,
  years,
  currentValue = 0,
  annualReturnRate = 0.03,
}) {
  const annualContrib = monthlyContribution * 12;
  const fvCurrent = currentValue * Math.pow(1 + annualReturnRate, years);
  const fvFlow =
    annualReturnRate === 0
      ? annualContrib * years
      : (annualContrib * (Math.pow(1 + annualReturnRate, years) - 1)) /
        annualReturnRate;
  return fvCurrent + fvFlow;
}
